import type { ApiClient } from '@acme/api-client'
import { inject, type InjectionKey } from 'vue'
import type * as T from './types'

type Id = string
type Opt = { signal?: AbortSignal }

// Monitoring API (monitoring-api/src/MonitoringService.Api), at /api/monitoramento. Every route is per tenant:
// /tenants/{t}/monitoring/…
export function createMonitoringApi(client: ApiClient) {
  const base = (tenantId: Id) => `tenants/${encodeURIComponent(tenantId)}/monitoring`
  const get = <R>(tenantId: Id, path: string, query?: Record<string, string | number | boolean | undefined>, signal?: AbortSignal) =>
    client.get<R>(`${base(tenantId)}/${path}`, { query, signal })
  const post = <R>(tenantId: Id, path: string, body?: unknown) => client.post<R>(`${base(tenantId)}/${path}`, body ?? {})
  const put = <R>(tenantId: Id, path: string, body?: unknown) => client.put<R>(`${base(tenantId)}/${path}`, body ?? {})

  return {
    // The caller's access (never 403: enabled=false when the Monitoring is off).
    access: (t: Id, o?: Opt) => get<T.MyAccessResponse>(t, 'access', undefined, o?.signal),

    // Alerts
    alerts: (t: Id, query: { status?: T.AlertStatus; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.AlertSummaryResponsePagedResponse>(t, 'alerts', query, o?.signal),
    alert: (t: Id, id: Id, o?: Opt) => get<T.AlertResponse>(t, `alerts/${id}`, undefined, o?.signal),
    createAlert: (t: Id, body: T.SaveAlertCommand) => post<T.AlertResponse>(t, 'alerts', body),
    updateAlert: (t: Id, id: Id, body: T.SaveAlertCommand) => put<T.AlertResponse>(t, `alerts/${id}`, body),
    deleteAlert: (t: Id, id: Id) => client.delete<void>(`${base(t)}/alerts/${id}`),
    pauseAlert: (t: Id, id: Id) => post<T.AlertResponse>(t, `alerts/${id}/pause`),
    resumeAlert: (t: Id, id: Id) => post<T.AlertResponse>(t, `alerts/${id}/resume`),

    // Editor
    testQuery: (t: Id, body: T.TestQueryCommand) => post<T.TestQueryResponse>(t, 'queries/test', body),
    variables: (t: Id, timeZone?: string, o?: Opt) => get<T.QueryVariableResponse[]>(t, 'queries/variables', { timeZone }, o?.signal),
    connection: (t: Id, o?: Opt) => get<T.MonitoringConnectionResponse>(t, 'connection', undefined, o?.signal),
    previewSchedule: (t: Id, body: T.ScheduleInput) => post<T.SchedulePreviewResponse>(t, 'schedule/preview', body),
    recipientCandidates: (t: Id, o?: Opt) => get<T.RecipientCandidateResponse[]>(t, 'recipients/candidates', undefined, o?.signal),

    // Runs
    runNow: (t: Id, alertId: Id, audience: T.RunAudience) => post<T.RunSummaryResponse>(t, `alerts/${alertId}/run`, { audience }),
    runs: (t: Id, alertId: Id, query: { page?: number; pageSize?: number }, o?: Opt) =>
      get<T.RunSummaryResponsePagedResponse>(t, `alerts/${alertId}/runs`, query, o?.signal),
    run: (t: Id, runId: Id, o?: Opt) => get<T.RunDetailResponse>(t, `runs/${runId}`, undefined, o?.signal),
    resend: (t: Id, runId: Id, audience: T.RunAudience) => post<T.RunSummaryResponse>(t, `runs/${runId}/resend`, { audience }),

    // Bell
    inbox: (t: Id, o?: Opt) => get<T.InboxResponse>(t, 'inbox', { pageSize: 50 }, o?.signal),
    markInboxRead: (t: Id, ids: string[]) => post<void>(t, 'inbox/read', { ids }),

    // The look of the e-mails (content width), for who configures the alerts.
    emailSettings: (t: Id, o?: Opt) => get<T.EmailSettingsResponse>(t, 'email-settings', undefined, o?.signal),
    saveEmailSettings: (t: Id, body: T.UpdateEmailSettingsCommand) => put<T.EmailSettingsResponse>(t, 'email-settings', body),
    // The AI profile of the summaries (Configurações › IA; null = the platform's model).
    aiSettings: (t: Id, o?: Opt) => get<T.AiSettingsResponse>(t, 'ai-settings', undefined, o?.signal),
    saveAiSettings: (t: Id, aiProfile: string | null) => put<T.AiSettingsResponse>(t, 'ai-settings', { aiProfile }),

    // Settings and roles
    settings: (t: Id, o?: Opt) => get<T.TenantSettingsResponse>(t, 'settings', undefined, o?.signal),
    saveSettings: (t: Id, body: T.UpdateTenantSettingsCommand) => put<T.TenantSettingsResponse>(t, 'settings', body),
    rolePermissions: (t: Id, o?: Opt) => get<T.RolePermissionsResponse[]>(t, 'roles/permissions', undefined, o?.signal),
    getRolePermissions: (t: Id, role: string, o?: Opt) =>
      get<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, undefined, o?.signal),
    saveRolePermissions: (t: Id, role: string, permissions: string[]) =>
      put<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, { permissions }),
  }
}

export type MonitoringApi = ReturnType<typeof createMonitoringApi>

export const MONITORING_API: InjectionKey<MonitoringApi> = Symbol('MonitoringApi')

export function useMonitoringApi(): MonitoringApi {
  const api = inject(MONITORING_API)
  if (!api) throw new Error('MONITORING_API was not provided.')
  return api
}

// Query keys: everything of one tenant's Monitoring starts with ['monitoring', tenantId].
export const monitoringKeys = {
  all: (tenantId: Id) => ['monitoring', tenantId] as const,
  access: (tenantId: Id) => ['monitoring', tenantId, 'access'] as const,
  area: (tenantId: Id, area: string) => ['monitoring', tenantId, area] as const,
}
